/**
 * Shared authenticated API client for the dashboard backend (/api/*).
 *
 * Centralizes the auth + transport concerns the protected proxies now require,
 * so components never duplicate auth logic and never touch the service-role key:
 *  - reads the current Supabase access token from the stored session
 *  - attaches `Authorization: Bearer <JWT>` + JSON content headers
 *  - enforces a request timeout and supports external cancellation
 *  - normalizes failures into a single ApiError (no_session / unauthorized /
 *    forbidden / rate_limited / timeout / cancelled / server_error / network)
 *  - preserves the existing /api/claude (streaming Response) and /api/places
 *    ({ place, places }) response contracts
 *
 * The token comes from localStorage["wn_session"].access_token (how the app
 * already persists the Supabase session). Never place the anon/service key here.
 */

const DEFAULT_TIMEOUT_MS = 30_000;
const BFF_CACHE = new Map();

export class ApiError extends Error {
  constructor(message, { status = 0, code = "error", cause } = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    if (cause) this.cause = cause;
  }
}

const STATUS_CODE = { 401: "unauthorized", 403: "forbidden", 413: "too_large", 429: "rate_limited" };

/** Read the current access token from the persisted session. Injectable for tests. */
export function getSessionToken(storage) {
  const store = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  try {
    const raw = store && store.getItem("wn_session");
    if (!raw) return null;
    const session = JSON.parse(raw);
    return (session && session.access_token) || null;
  } catch {
    return null;
  }
}

/**
 * Authenticated fetch against a backend endpoint.
 * @param {string} path e.g. "/api/places"
 * @param {{
 *   method?: string, body?: any, signal?: AbortSignal, timeoutMs?: number,
 *   token?: string|null, raw?: boolean, fetchImpl?: typeof fetch, storage?: Storage,
 * }} [opts]
 * @returns {Promise<any|Response>} parsed body, or the raw Response when raw:true
 * @throws {ApiError}
 */
export async function apiFetch(path, opts = {}) {
  const {
    method = "GET",
    body,
    signal,
    timeoutMs = DEFAULT_TIMEOUT_MS,
    raw = false,
    fetchImpl = typeof fetch !== "undefined" ? fetch : null,
    storage,
  } = opts;
  const token = opts.token !== undefined ? opts.token : getSessionToken(storage);

  if (!fetchImpl) throw new ApiError("fetch is unavailable", { code: "network" });
  if (!token) throw new ApiError("No active session", { status: 0, code: "no_session" });

  // Track abort cause with explicit flags rather than AbortSignal.reason, which
  // is not reliably preserved across runtimes (Node/jsdom drop the abort reason).
  const controller = new AbortController();
  let timedOut = false;
  let cancelled = false;
  const timer = setTimeout(() => { timedOut = true; controller.abort(); }, timeoutMs);
  if (signal) {
    if (signal.aborted) { cancelled = true; controller.abort(); }
    else signal.addEventListener("abort", () => { cancelled = true; controller.abort(); }, { once: true });
  }

  let res;
  try {
    res = await fetchImpl(path, {
      method,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted) {
      if (timedOut) throw new ApiError("Request timed out", { code: "timeout", cause: err });
      throw new ApiError("Request cancelled", { code: "cancelled", cause: err });
    }
    throw new ApiError("Network error", { status: 0, code: "network", cause: err });
  } finally {
    clearTimeout(timer);
  }

  if (!res.ok) {
    const code = STATUS_CODE[res.status] || (res.status >= 500 ? "server_error" : "error");
    let message = `Request failed (${res.status})`;
    try {
      const j = await res.clone().json();
      if (j && j.error && j.error.message) message = j.error.message;
    } catch {
      /* non-JSON error body; keep generic message */
    }
    throw new ApiError(message, { status: res.status, code });
  }

  if (raw) return res;
  const ct = res.headers.get("content-type") || "";
  if (!ct.toLowerCase().includes("application/json")) {
    throw new ApiError("API returned a non-JSON response", {
      status: res.status,
      code: "invalid_response",
    });
  }
  return res.json();
}

function unwrapData(value) {
  return value && typeof value === "object" && !Array.isArray(value) && "data" in value
    ? value.data
    : value;
}

export async function getOmanMerchantReviews(placeId, options = {}) {
  const env = await apiFetch(`/api/oman/merchants/${encodeURIComponent(placeId)}/reviews`, options);
  if (env && env.ok === false) throw new ApiError(env.error?.message || "Request failed", { code: env.error?.code || "error" });
  return unwrapData(env);
}

/** All Saudi city merchant rows (legacy per-city row shape) via the authenticated BFF. */
export async function getKsaMerchantRows(options = {}) {
  const env = await apiFetch("/api/merchants/ksa-rows", { timeoutMs: 60_000, ...options, method: "POST", body: {} });
  if (env && env.ok === false) throw new ApiError(env.error?.message || "Request failed", { code: env.error?.code || "error" });
  const data = unwrapData(env);
  return Array.isArray(data?.rows) ? data.rows : [];
}

export async function getOmanStatuses(placeIds, options = {}) {
  const env = await apiFetch("/api/oman/status-bulk", { ...options, method: "POST", body: { place_ids: placeIds || [] } });
  if (env && env.ok === false) throw new ApiError(env.error?.message || "Request failed", { code: env.error?.code || "error" });
  return unwrapData(env);
}

/** Saudi status hydration uses the authenticated BFF, never browser PostgREST. */
export async function getKsaStatuses(keys, options = {}) {
  const env = await apiFetch("/api/merchant-status/bulk", {
    ...options,
    method: "POST",
    body: { keys: Array.isArray(keys) ? keys : [] },
  });
  if (env && env.ok === false) throw new ApiError(env.error?.message || "Request failed", { code: env.error?.code || "error" });
  return unwrapData(env);
}

export async function saveKsaStatus(payload, options = {}) {
  const env = await apiFetch("/api/merchant-status", { ...options, method: "POST", body: payload });
  if (env && env.ok === false) throw new ApiError(env.error?.message || "Request failed", { code: env.error?.code || "error" });
  return unwrapData(env);
}

export async function saveOmanStatus(payload, options = {}) {
  const env = await apiFetch("/api/oman/status", { ...options, method: "POST", body: payload });
  if (env && env.ok === false) throw new ApiError(env.error?.message || "Request failed", { code: env.error?.code || "error" });
  return unwrapData(env);
}

/**
 * Call the Claude proxy. Returns the raw (possibly streaming SSE) Response so
 * callers can read `response.body` exactly as before.
 */
export function callClaude({ system, prompt, stream = false, signal, token, fetchImpl, storage } = {}) {
  return apiFetch("/api/claude", {
    method: "POST",
    body: { system, prompt, stream },
    signal,
    token,
    fetchImpl,
    storage,
    raw: true,
  });
}

/** Call the Places proxy. Returns { place, places } (unchanged shape). */
export function searchPlace({ query, signal, token, fetchImpl, storage } = {}) {
  return apiFetch("/api/places", {
    method: "POST",
    body: { query },
    signal,
    token,
    fetchImpl,
    storage,
    raw: false,
  });
}

/**
 * BFF helper — POST a JSON body to a merchant-platform endpoint and unwrap the
 * standard { ok, data } envelope. Non-2xx already throws ApiError in apiFetch;
 * an envelope with ok:false also throws. Returns the `data` payload.
 */
async function callBff(path, body, opts = {}) {
  const { cacheKey, cacheTtlMs = 0, ...fetchOpts } = opts;
  const now = Date.now();
  if (cacheKey && cacheTtlMs > 0) {
    const cached = BFF_CACHE.get(cacheKey);
    if (cached && cached.expiresAt > now) return cached.promise;
  }
  const promise = apiFetch(path, { method: "POST", body: body || {}, raw: false, ...fetchOpts })
    .then((env) => {
      if (env && env.ok === false) {
        throw new ApiError((env.error && env.error.message) || "Request failed", {
          code: (env.error && env.error.code) || "error",
        });
      }
      return unwrapData(env);
    })
    .catch((error) => {
      if (cacheKey) BFF_CACHE.delete(cacheKey);
      throw error;
    });
  if (cacheKey && cacheTtlMs > 0) BFF_CACHE.set(cacheKey, { promise, expiresAt: now + cacheTtlMs });
  return promise;
}

/** Clear only the in-memory BFF cache; sensitive responses never reach localStorage. */
export function clearBffCache(prefix = "") {
  for (const key of BFF_CACHE.keys()) if (!prefix || key.startsWith(prefix)) BFF_CACHE.delete(key);
}

/** Merchant-platform (BFF) contract helpers — the ONLY way the new frontend reads merchant data. */
export const getDashboardSummary = (params = {}, opts = {}) => callBff("/api/dashboard/summary", params, opts);
export const getHomeSummary = (opts = {}) => apiFetch("/api/home/summary", { method: "GET", raw: false, ...opts }).then(unwrapData);
export const getAccessStatus = (opts = {}) => apiFetch("/api/access/status", { method: "GET", raw: false, ...opts }).then(unwrapData);
export const requestModuleAccess = (module_key, business_reason = "", opts = {}) => apiFetch("/api/access/requests", { method: "POST", body: { module_key, requested_capability: "read", business_reason }, raw: false, ...opts }).then(unwrapData);
export const getMyAccessRequests = (opts = {}) => apiFetch("/api/access/requests", { method: "GET", raw: false, ...opts }).then(unwrapData);
export const listNexusUsers = (opts = {}) => apiFetch("/api/admin/users", { method: "GET", raw: false, ...opts }).then(unwrapData);
export const updateNexusUser = (payload, opts = {}) => apiFetch("/api/admin/users", { method: "POST", body: payload, raw: false, ...opts }).then(unwrapData);
export const listAccessRequests = (opts = {}) => apiFetch("/api/admin/access-requests", { method: "GET", raw: false, ...opts }).then(unwrapData);
export const decideAccessRequest = (payload, opts = {}) => apiFetch("/api/admin/access-requests", { method: "POST", body: payload, raw: false, ...opts }).then(unwrapData);
export const grantNexusEntitlement = (payload, opts = {}) => apiFetch("/api/admin/entitlements", { method: "POST", body: payload, raw: false, ...opts }).then(unwrapData);
export const revokeNexusEntitlement = (payload, opts = {}) => apiFetch("/api/admin/entitlements", { method: "DELETE", body: payload, raw: false, ...opts }).then(unwrapData);
export const listNexusAudit = (opts = {}) => apiFetch("/api/admin/audit", { method: "GET", raw: false, ...opts }).then(unwrapData);
export const getMarketOverview = (params = {}, opts = {}) => callBff("/api/dashboard/market-overview", params, opts);
export const getGeography = (opts = {}) => callBff("/api/geography", {}, opts);
export const listMerchants = (params = {}, opts = {}) => callBff("/api/merchants/list", params, opts);
export async function getOmanPublicationMerchants(opts = {}) {
  const env = await apiFetch("/api/oman/merchants", { method: "GET", raw: false, ...opts });
  if (env && env.ok === false) {
    throw new ApiError((env.error && env.error.message) || "Request failed", {
      code: (env.error && env.error.code) || "error",
    });
  }
  return unwrapData(env);
}
export const getMerchantDetail = (id, opts = {}) => callBff("/api/merchants/detail", { id }, opts);
export const getMerchantProfile = (id, opts = {}) => callBff("/api/merchants/profile", { id }, opts);
export const getMerchantCapabilities = (opts = {}) => callBff("/api/merchants/capabilities", {}, opts);
export const semanticSearchMerchants = (params = {}, opts = {}) => callBff("/api/merchants/semantic-search", params, opts);
export const listMerchantNotes = (merchantId, opts = {}) => callBff("/api/merchants/notes", { merchantId }, opts);
export const addMerchantNote = (merchantId, body, opts = {}) => callBff("/api/merchants/notes-create", { merchantId, body }, opts);
export const getMerchantNoteAttachmentUrl = (merchantId, attachmentId, opts = {}) =>
  callBff("/api/merchants/notes-attachment-url", { merchantId, attachmentId }, opts);
// Acquisition Pipeline (G4)
export const listPipeline = (params = {}, opts = {}) => callBff("/api/pipeline/list", params, opts);
export const getPipelineSummary = (params = {}, opts = {}) => callBff("/api/pipeline/summary", params, opts);
export const updatePipelineStatus = (params = {}, opts = {}) => callBff("/api/pipeline/status", params, opts);
export const assignPipeline = (params = {}, opts = {}) => callBff("/api/pipeline/assign", params, opts);
export const getMerchantActivity = (merchantId, opts = {}) => callBff("/api/pipeline/activity", { merchantId }, opts);
// Venues / Malls (G5)
export const listVenues = (params = {}, opts = {}) => callBff("/api/venues/list", params, opts);

/** Warm small, high-value reads after authenticated access is established. */
export async function prefetchWorkspaceData({ token, countries = ["SA", "OM"], fetchImpl } = {}) {
  if (!token) return { warmed: [], failed: [] };
  const jobs = [
    ["sa:overview", () => getMarketOverview({ country: "SA" }, { token, fetchImpl, cacheKey: "market:SA", cacheTtlMs: 60_000 })],
    ["sa:venues", () => listVenues({ country: "SA" }, { token, fetchImpl, cacheKey: "venues:SA::", cacheTtlMs: 60_000 })],
  ];
  if (countries.includes("OM")) {
    jobs.push(
      ["om:overview", () => getMarketOverview({ country: "OM" }, { token, fetchImpl, cacheKey: "market:OM", cacheTtlMs: 60_000 })],
      ["om:venues", () => listVenues({ country: "OM" }, { token, fetchImpl, cacheKey: "venues:OM::", cacheTtlMs: 60_000 })],
    );
  }
  const warmed = [], failed = [];
  let cursor = 0;
  async function worker() {
    while (cursor < jobs.length) {
      const job = jobs[cursor++];
      try { await job[1](); warmed.push(job[0]); }
      catch { failed.push(job[0]); }
    }
  }
  await Promise.all([worker(), worker()]);
  if (typeof window !== "undefined") window.__NEXUS_PREFETCH_METRICS__ = { warmed: warmed.slice(), failed: failed.slice(), completed_at: Date.now() };
  return { warmed, failed };
}
// Saved Merchants (G6) — identity is the JWT; no user field is ever sent.
export const listSaved = (params = {}, opts = {}) => callBff("/api/merchants/saved", params, opts);
export const saveMerchant = (merchantId, opts = {}) => callBff("/api/merchants/save", { merchantId }, opts);
export const removeSaved = (merchantId, opts = {}) => callBff("/api/merchants/unsave", { merchantId }, opts);
